/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { CSSObject } from "@emotion/css";
import { css, cx } from "@emotion/css";
import Button from "@material-ui/core/Button";
import { borderRadius, text, themeTokens, space } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import { useIsBusy } from "../../hooks/useIsBusy";
import { noOp } from "../../utils/noOp";
import type { MenuTargetAriaAttributes } from "../Menu/useMenuState";
export interface ActionButtonProps {
    /**
     * The label of the button.
     *
     * This is not shown if any children are passed to the button.
     */
    label: string;
    /**
     * The accessible name of the button.
     */
    accessibleName?: string;
    /**
     * The title of the button.
     *
     * This is shown when hovering over the button.
     */
    title?: string;
    /**
     * When `true`, the `title` is not set on the button.
     */
    hideTitle?: boolean;
    /**
     * The label to show when the button is busy processing a click.
     */
    busyLabel?: string;
    /**
     * Controls whether the button is disabled.
     *
     * - Using a `boolean` will directly control whether the button is disabled.
     * - Using a `Promise` will set the button to disabled until the promise has been fulfilled.
     *
     * The button is automatically disabled when busy.
     */
    disabled?: Promise<unknown> | boolean | null;
    /**
     * Controls the appearance of the button.
     */
    type?: ActionButtonType;
    /**
     * The icon to show next to the label.
     */
    icon?: JSX.Element;
    /**
     * The position of the icon relative to the label.
     */
    iconPosition?: "left" | "right";
    /**
     * The tab index of the button.
     *
     * When omitted, the button will be keyboard focusable in sequential order.
     * This can only be set to `-1` which disables the keyboard focusability of the button.
     */
    tabIndex?: -1;
    /**
     * Sets the form owner of the button element
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#form
     */
    formId?: string;
    /**
     * Controls whether the button is automatically focused when shown.
     */
    keyboardFocused?: boolean;
    /**
     * The action to perform when the button is focused.
     */
    onFocus?(event: React.FocusEvent<HTMLElement>): void;
    /**
     * The action to perform when the button is clicked.
     *
     * If this function returns a promise, the button will be set to busy while the promise is pending.
     * When the button is busy, it is disabled and shows the `busyLabel` if provided.
     */
    onClick?(event: any): Promise<unknown> | void;
    /**
     * If the button opens a menu, these ARIA attributes should be provided for accessibility.
     */
    menuButtonAttributes?: MenuTargetAriaAttributes;
}
export enum ActionButtonType {
    Primary = "Primary",
    Secondary = "Secondary",
    Ternary = "Ternary",
    Save = "Save",
    Delete = "Delete",
    CreateRelease = "CreateRelease",// Edge case unique to our design.
    Category = "Category"
}
export function ActionButton({ label, accessibleName, title, hideTitle, busyLabel, disabled: disabledPromise, type, icon, iconPosition = "left", tabIndex, keyboardFocused, onFocus, onClick, formId, menuButtonAttributes }: ActionButtonProps) {
    const [onClickInvocation, setOnClickInvocation] = React.useState<Promise<unknown> | undefined>();
    const invokePromiseOnClick = React.useCallback(async (event: any) => {
        if (!onClick) {
            return;
        }
        if (event.preventDefault) {
            event.preventDefault();
        }
        const promise = onClick(event);
        setOnClickInvocation(promise ?? undefined);
    }, [onClick]);
    const isBusy = useIsBusy(onClickInvocation);
    const isDisabled = useIsBusy(disabledPromise) || isBusy;
    const getButton = () => {
        const labelToShow = isBusy && busyLabel ? busyLabel : label;
        const titleToShow = title ? title : labelToShow;
        const otherPropsForButtons = {
            tabIndex: tabIndex,
            "aria-label": accessibleName,
        };
        const handleOnClick = isDisabled ? noOp : (e: any) => invokePromiseOnClick(e);
        switch (type) {
            case ActionButtonType.Primary:
                return (<Button variant="contained" disableElevation={true} disableRipple={true} type="submit" title={hideTitle ? undefined : titleToShow} disabled={isDisabled} startIcon={iconPosition === "left" ? icon : undefined} endIcon={iconPosition === "right" ? icon : undefined} onClick={handleOnClick} onFocus={onFocus} autoFocus={keyboardFocused} form={formId} className={primaryButtonStyles} {...otherPropsForButtons} {...menuButtonAttributes}>
                        {labelToShow}
                    </Button>);
            case ActionButtonType.Category:
                return (<Button variant="contained" disableElevation={true} disableRipple={true} type="submit" title={hideTitle ? undefined : titleToShow} disabled={isDisabled} startIcon={iconPosition === "left" ? icon : undefined} endIcon={iconPosition === "right" ? icon : undefined} onClick={handleOnClick} onFocus={onFocus} autoFocus={keyboardFocused} form={formId} className={categoryButtonStyles} {...otherPropsForButtons} {...menuButtonAttributes}>
                        {labelToShow}
                    </Button>);
            case ActionButtonType.CreateRelease:
                return (<Button variant="contained" disableElevation={true} disableRipple={true} type="submit" title={hideTitle ? undefined : titleToShow} disabled={isDisabled} onClick={handleOnClick} autoFocus={keyboardFocused} form={formId} className={createReleaseButtonStyles} {...otherPropsForButtons} {...menuButtonAttributes}>
                        {labelToShow}
                    </Button>);
            case ActionButtonType.Save:
                return (<Button variant="contained" disableElevation={true} disableRipple={true} type="submit" title={hideTitle ? undefined : titleToShow} disabled={isDisabled} onClick={handleOnClick} autoFocus={keyboardFocused} form={formId} className={primaryButtonStyles} {...otherPropsForButtons} {...menuButtonAttributes}>
                        {labelToShow}
                    </Button>);
            case ActionButtonType.Delete:
                return (<Button variant="contained" disableElevation={true} disableRipple={true} type="submit" title={hideTitle ? undefined : titleToShow} disabled={isDisabled} onClick={handleOnClick} autoFocus={keyboardFocused} form={formId} className={deleteButtonStyles} {...otherPropsForButtons} {...menuButtonAttributes}>
                        {labelToShow}
                    </Button>);
            case ActionButtonType.Ternary:
                return (<Button disableElevation={true} disableRipple={true} title={hideTitle ? undefined : titleToShow} startIcon={iconPosition === "left" ? icon : undefined} endIcon={iconPosition === "right" ? icon : undefined} disabled={isDisabled} onClick={handleOnClick} onFocus={onFocus} autoFocus={keyboardFocused} form={formId} className={ternaryButtonStyles} {...otherPropsForButtons} {...menuButtonAttributes}>
                        {labelToShow}
                    </Button>);
            // Fallthrough to Secondary
            default:
                return (<Button variant="contained" disableElevation={true} disableRipple={true} title={hideTitle ? undefined : titleToShow} startIcon={iconPosition === "left" ? icon : undefined} endIcon={iconPosition === "right" ? icon : undefined} disabled={isDisabled} onClick={handleOnClick} onFocus={onFocus} autoFocus={keyboardFocused} form={formId} className={secondaryButtonStyles} {...otherPropsForButtons} {...menuButtonAttributes}>
                        {labelToShow}
                    </Button>);
        }
    };
    return getButton();
}
const buttonClasses = {
    root: "MuiButton-root",
    label: "MuiButton-label",
    startIcon: "MuiButton-startIcon",
    endIcon: "MuiButton-endIcon",
    disabled: "Mui-disabled",
};
const iconClasses = {
    root: "MuiSvgIcon-root",
};
const buttonPaddingHorizontal = "10px";
const buttonPaddingVertical = "6px";
export const actionButtonRootCss: CSSObject = {
    flexShrink: 0,
    height: "2.25rem", // Otherwise the additional border can force the height to increase.
    minWidth: "5rem",
    borderRadius: borderRadius.small,
    fill: "currentcolor",
    cursor: "pointer",
    display: "inline-flex",
    position: "relative",
    alignItems: "center",
    userSelect: "none",
    justifyContent: "center",
    boxSizing: "border-box",
    font: text.body.bold.medium,
    textTransform: "none",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    letterSpacing: 0,
    transitionProperty: "background",
    transitionDuration: "0.2s",
    ":focus-visible": {
        boxShadow: themeTokens.shadow.focused,
    },
};
export const actionButtonDisabledCss: CSSObject = {
    backgroundColor: "transparent",
    color: themeTokens.color.button.text.disabled,
    cursor: "not-allowed",
    pointerEvents: "auto", // MUI disables pointer events for disabled buttons which prevents the cursor from changing, so we need to reset pointer events to auto
    ":hover": {
        backgroundColor: "transparent",
        color: themeTokens.color.button.text.disabled,
    },
};
export const actionButtonDisabledWithBackgroundCss: CSSObject = {
    backgroundColor: themeTokens.color.button.background.disabled,
    border: `1px solid ${themeTokens.color.button.border.disabled}`,
    padding: `calc(${buttonPaddingVertical} - 1px) calc(${buttonPaddingHorizontal} - 1px)`, // Subtract border width from padding to avoid content shifting
    ":hover": {
        // MUI 4 annoyingly sets a background color on hover for disabled buttons so we need to override this.
        backgroundColor: themeTokens.color.button.background.disabled,
    },
};
export const primaryActionButtonCss = getButtonCss({
    textColor: themeTokens.color.button.text.primary,
    backgroundColor: themeTokens.color.button.background.primary,
});
export const secondaryActionButtonCss = getButtonCss({
    textColor: themeTokens.color.button.text.secondary,
    backgroundColor: themeTokens.color.button.background.secondary,
    borderColor: themeTokens.color.button.border.secondary,
});
export const ternaryActionButtonCss = getButtonCss({
    textColor: themeTokens.color.button.text.quiet,
    backgroundColor: themeTokens.color.button.background.quiet,
});
export const loudActionButtonCss = getButtonCss({
    textColor: themeTokens.color.button.text.loud,
    backgroundColor: themeTokens.color.button.background.loud,
});
const deleteActionButtonCss = getButtonCss({
    textColor: themeTokens.color.button.text.destructive,
    backgroundColor: themeTokens.color.button.background.destructive,
});
const createReleaseActionButtonCss = getButtonCss({
    textColor: themeTokens.color.button.text.loud,
    backgroundColor: themeTokens.color.button.background.loud,
});
const actionButtonIconStyles = css({
    [`&.${buttonClasses.root}`]: {
        [`.${iconClasses.root}`]: {
            fontSize: 24,
            // Some of our icon components hardcode margins into them for layout purposes elsewhere, we don't want this affecting the button layout
            margin: 0,
        },
        [`.${buttonClasses.startIcon}`]: {
            marginRight: space[4],
        },
        [`.${buttonClasses.endIcon}`]: {
            marginLeft: space[4],
            // The MUI 0 ActionButton shows the right icon with less space on the right since this is usually the DropDownIcon which has lots of padding built in.
            // So here we increase the default MUI4 marginRight from -4 to -8 to create similar spacing.
            marginRight: `-${space[8]}`,
        },
    },
});
const actionButtonRootStyles = cx(actionButtonIconStyles, css({
    [`&.${buttonClasses.root}`]: actionButtonRootCss,
}));
const actionButtonDisabledStyles = css({
    [`&.${buttonClasses.root}.${buttonClasses.disabled}`]: actionButtonDisabledCss,
});
const actionButtonDisabledWithBackgroundStyles = cx(actionButtonDisabledStyles, css({
    [`&.${buttonClasses.root}.${buttonClasses.disabled}`]: actionButtonDisabledWithBackgroundCss,
}));
const primaryButtonStyles = cx(actionButtonRootStyles, actionButtonDisabledWithBackgroundStyles, css({
    [`&.${buttonClasses.root}`]: primaryActionButtonCss,
}));
const secondaryButtonStyles = cx(actionButtonRootStyles, actionButtonDisabledWithBackgroundStyles, css({
    [`&.${buttonClasses.root}`]: secondaryActionButtonCss,
}));
const ternaryButtonStyles = cx(actionButtonRootStyles, actionButtonDisabledStyles, css({
    [`&.${buttonClasses.root}`]: ternaryActionButtonCss,
}));
const deleteButtonStyles = cx(actionButtonRootStyles, actionButtonDisabledWithBackgroundStyles, css({
    [`&.${buttonClasses.root}`]: deleteActionButtonCss,
}));
const createReleaseButtonStyles = cx(actionButtonRootStyles, actionButtonDisabledWithBackgroundStyles, css({
    [`&.${buttonClasses.root}`]: createReleaseActionButtonCss,
}));
const categoryButtonStyles = cx(secondaryButtonStyles, css({
    [`&.${buttonClasses.root}`]: {
        border: "none",
    },
}));
interface CommonButtonTokens {
    backgroundColor: {
        default: string;
        hover: string;
        pressed: string;
    };
    textColor: string;
    borderColor?: string;
}
function getButtonCss(buttonTokens: CommonButtonTokens) {
    return {
        backgroundColor: buttonTokens.backgroundColor.default,
        color: buttonTokens.textColor,
        border: buttonTokens.borderColor ? `1px solid ${buttonTokens.borderColor}` : undefined,
        padding: buttonTokens.borderColor ? `calc(${buttonPaddingVertical} - 1px) calc(${buttonPaddingHorizontal} - 1px)` : `${buttonPaddingVertical} ${buttonPaddingHorizontal}`, // Subtract border width from padding to avoid content shifting
        ":hover": {
            backgroundColor: buttonTokens.backgroundColor.hover,
            color: buttonTokens.textColor,
        },
        ":active": {
            backgroundColor: buttonTokens.backgroundColor.pressed,
            color: buttonTokens.textColor,
        },
        "&[aria-expanded=true]": {
            backgroundColor: buttonTokens.backgroundColor.pressed,
            color: buttonTokens.textColor,
        },
    };
}
